/**
 * Text-editing rules shared by the editable controls.
 *
 * Pure functions over a string and an index, so `TextBox` and `TextArea`
 * navigate identically and the rules can be tested without a GPU. Indices are
 * UTF-16 code units throughout, matching the hidden textarea the clipboard
 * and IME go through.
 */

/**
 * Room kept past the end of the text, and the margin the caret is scrolled to.
 *
 * The end of the text is a caret position like any other, and it is the only one with nothing
 * after it to make room for it. With the scroll stopping at the text's own width there is
 * nowhere left to scroll to, so the caret comes to rest on the edge of the rect the field clips
 * to and is not drawn at all - and the end of a line reads as somewhere the caret will not go.
 * It goes there; it cannot be seen doing it.
 *
 * Here rather than in either control because both need it to mean the same thing, and because
 * it has to be the *same* number in every place that measures from it: what a caret is scrolled
 * to, what the scrollable extent reserves, and what wrapping stops short of. One number is what
 * makes the space asked for space that exists. `TextBox` was written without it and had the bug
 * this comment describes; `TextArea` had it fixed and kept its own copy, which is how the two
 * came apart.
 */
export const CARET_ROOM = 12;

export type CharClass = "space" | "word" | "punct";

export function classifyChar(ch: string): CharClass {
  if (/\s/.test(ch)) return "space";
  if (/[\p{L}\p{N}_]/u.test(ch)) return "word";
  return "punct";
}

/** Step one code point, so a surrogate pair moves as a unit. */
export function stepIndex(text: string, index: number, direction: 1 | -1): number {
  if (direction > 0) {
    if (index >= text.length) return text.length;
    const code = text.codePointAt(index);
    return index + (code !== undefined && code > 0xffff ? 2 : 1);
  }
  if (index <= 0) return 0;
  const prev = text.codePointAt(index - 2);
  return index - (index >= 2 && prev !== undefined && prev > 0xffff ? 2 : 1);
}

/**
 * Next word boundary in `direction`, the Windows way: step over the run you
 * are in, then over any spaces that follow it.
 */
export function wordBoundary(text: string, index: number, direction: 1 | -1): number {
  let i = index;
  if (direction > 0) {
    if (i >= text.length) return text.length;
    const kind = classifyChar(text[i]!);
    while (i < text.length && classifyChar(text[i]!) === kind) i++;
    while (i < text.length && classifyChar(text[i]!) === "space") i++;
    return i;
  }
  if (i <= 0) return 0;
  while (i > 0 && classifyChar(text[i - 1]!) === "space") i--;
  if (i === 0) return 0;
  const kind = classifyChar(text[i - 1]!);
  while (i > 0 && classifyChar(text[i - 1]!) === kind) i--;
  return i;
}

/** Extent of the run under `index`, which is what a double-click selects. */
export function wordAt(text: string, index: number): { start: number; end: number } {
  if (text.length === 0) return { start: 0, end: 0 };
  const at = Math.min(Math.max(index, 0), text.length - 1);
  const kind = classifyChar(text[at]!);
  let start = at;
  let end = at;
  while (start > 0 && classifyChar(text[start - 1]!) === kind) start--;
  while (end < text.length && classifyChar(text[end]!) === kind) end++;
  return { start, end };
}

/** A buffer and where the caret and its anchor were sitting in it. */
export interface EditState {
  text: string;
  caret: number;
  anchor: number;
}

/**
 * How an edit was made, which is what decides whether it joins the one before it.
 *
 * `other` never joins: a paste, a newline, or anything replacing a selection is its own step,
 * because those are the edits a person thinks of as one action.
 */
export type EditKind = "insert" | "delete" | "other";

const DEFAULT_LIMIT = 200;

/**
 * Undo for a flat text buffer.
 *
 * Whole-buffer snapshots rather than a diff. An editable buffer is at most a few tens of
 * kilobytes, so the simple thing is also the fast thing, and unlike a per-operation log it
 * cannot miss an edit that reached the text by some other route.
 *
 * What it does have to be careful about is *coalescing*: an undo that gives back one character
 * at a time is not undo, it is a replay. A run of single-character edits of the same kind, each
 * continuing where the last one stopped, collapses into one step; moving the caret, changing
 * direction, or any edit that is not a plain keystroke ends the run.
 *
 * Kept here rather than inside a control so the rule is testable on its own, and so `TextBox`
 * can take the same one when it wants it.
 */
export class UndoStack {
  #undo: EditState[] = [];
  #redo: EditState[] = [];
  #limit: number;
  /** The kind of the last edit, and where its caret finished. */
  #last: { kind: EditKind; at: number } | null = null;
  /** Set by `joinNext`, and true for exactly one edit. */
  #join = false;

  constructor(limit = DEFAULT_LIMIT) {
    this.#limit = Math.max(1, limit);
  }

  get canUndo(): boolean {
    return this.#undo.length > 0;
  }

  get canRedo(): boolean {
    return this.#redo.length > 0;
  }

  /** Steps that would be given back, for a test or an enabled state. */
  get depth(): number {
    return this.#undo.length;
  }

  clear(): void {
    this.#undo.length = 0;
    this.#redo.length = 0;
    this.#last = null;
  }

  /**
   * Note the state *before* an edit that is about to happen.
   *
   * `from` is where the caret was, `to` where it ends up; together they say whether this edit
   * carries on from the previous one.
   */
  record(before: EditState, kind: EditKind, from: number, to: number): void {
    // Nothing to join to is not a join: the first edit in a session still has to record the
    // state before it, or there would be no way back to an empty buffer.
    const joins = (this.#join && this.#undo.length > 0) || this.#joins(kind, from);
    this.#join = false;
    if (!joins) {
      this.#undo.push(before);
      // Oldest first: a cap that dropped the newest would make the most recent edit the one
      // you could not take back.
      if (this.#undo.length > this.#limit) this.#undo.shift();
    }
    this.#last = { kind, at: to };
    this.#redo.length = 0;
  }

  /** End the current run, so the next edit starts a step of its own. */
  breakRun(): void {
    this.#last = null;
    this.#join = false;
  }

  /**
   * Fold the next edit into the step before it, whatever kind either of them is.
   *
   * For an edit a person did not ask for and would not think of as one of their own: an editor
   * laying out what was just pasted, say. Undo has to give back what was there before the
   * paste, not the paste in a tidier arrangement - and coalescing cannot answer that on its
   * own, because both halves are the kind that never joins.
   */
  joinNext(): void {
    this.#join = true;
  }

  /** The state to go back to, or null when there is nothing left. */
  undo(current: EditState): EditState | null {
    const previous = this.#undo.pop();
    if (!previous) return null;
    this.#redo.push(current);
    this.#last = null;
    return previous;
  }

  redo(current: EditState): EditState | null {
    const next = this.#redo.pop();
    if (!next) return null;
    this.#undo.push(current);
    this.#last = null;
    return next;
  }

  #joins(kind: EditKind, from: number): boolean {
    if (kind === "other" || this.#last === null) return false;
    return this.#last.kind === kind && this.#last.at === from;
  }
}
